import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FolderKanban, MessageSquare, ListTodo, Plus, Loader2, ArrowLeft, Archive, Lock, Globe2, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useWorkspaceProjects, notifyWorkspaceChanged, useWorkspaceChanged } from "@/lib/workspaceProjects";
import ProjectSharing, { VISIBILITY } from "@/components/workspace/ProjectSharing";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const errMsg = (e, fallback) => e?.response?.data?.detail?.message || e?.response?.data?.detail?.error || fallback;

// Projects: Workspace projects + classic projects in ONE list. Adding a chat or a
// task to a classic project adopts it onto the canonical path first (server-side).
export default function ProjectsArea({ sub }) {
  const navigate = useNavigate();
  const { projects, reload } = useWorkspaceProjects();
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState({ conversations: [], tasks: [] });
  const [intent, setIntent] = useState("");
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (sub && projects.length) setSelected(projects.find((p) => p.project_id === sub) || null);
    else if (!sub) setSelected(null);
  }, [sub, projects]);

  const loadDetail = useCallback(async (p) => {
    if (!p?.project_id) { setDetail({ conversations: [], tasks: [] }); return; }
    try {
      const [c, t] = await Promise.all([
        axios.get(`${API}/workspace/conversations`, { params: { project_id: p.project_id } }),
        axios.get(`${API}/canonical/tasks`, { params: { project_id: p.project_id } }),
      ]);
      setDetail({ conversations: c.data.conversations || [], tasks: t.data.tasks || [] });
    } catch { setDetail({ conversations: [], tasks: [] }); }
  }, []);
  useEffect(() => { loadDetail(selected); }, [selected, loadDetail]);
  useWorkspaceChanged(useCallback(() => loadDetail(selected), [selected, loadDetail]));

  const open = (p) => (p.project_id ? navigate(`/app/projects/${p.project_id}`) : setSelected(p));
  const back = () => { setSelected(null); navigate("/app/projects"); };

  // Classic project → canonical project (idempotent) before any new work is attached.
  const ensureCanonical = async (p) => {
    if (p.project_id) return p.project_id;
    const { data } = await axios.post(`${API}/workspace/projects/adopt`, { legacy_id: p.legacy_id });
    notifyWorkspaceChanged();
    navigate(`/app/projects/${data.project_id}`);
    return data.project_id;
  };

  const newChat = async () => {
    setBusy(true);
    try {
      const pid = await ensureCanonical(selected);
      const { data } = await axios.post(`${API}/workspace/conversations`, { project_id: pid, title: `${selected.name} — chat` });
      toast.success("Chat created in this project");
      notifyWorkspaceChanged();
      navigate(`/app/chat/${data.conversation_id}`);
    } catch (e) { toast.error(errMsg(e, "Could not create the chat.")); }
    finally { setBusy(false); }
  };

  const newTask = async () => {
    if (!intent.trim()) return;
    setBusy(true);
    try {
      const pid = await ensureCanonical(selected);
      await axios.post(`${API}/workspace/projects/${pid}/tasks`, { intent: intent.trim() });
      toast.success("Draft Task added to this project");
      setIntent("");
      notifyWorkspaceChanged();
      await loadDetail({ project_id: pid });
    } catch (e) { toast.error(errMsg(e, "Could not create the task.")); }
    finally { setBusy(false); }
  };

  const createProject = async (form) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/workspace/projects`, form);
      toast.success(`Project “${data.name}” created`);
      setCreating(false);
      notifyWorkspaceChanged();
      navigate(`/app/projects/${data.project_id}`);
    } catch (e) { toast.error(errMsg(e, "Could not create the project.")); }
    finally { setBusy(false); }
  };

  const saveSharing = async (form) => {
    setBusy(true);
    try {
      const { data } = await axios.patch(`${API}/workspace/projects/${selected.project_id}`, form);
      toast.success(`Sharing updated: ${VISIBILITY[data.visibility]?.label || data.visibility}`);
      notifyWorkspaceChanged();
      await reload();
    } catch (e) { toast.error(errMsg(e, "Could not update sharing.")); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen" data-testid="projects-area">
      <div className="px-8 pt-8 pb-5 flex items-center gap-3">
        <FolderKanban className="w-6 h-6 text-[var(--qn-primary)]" />
        <div>
          <h1 className="text-xl font-semibold">Projects</h1>
          <p className="text-sm text-[var(--qn-foreground-muted)]">Every chat inside a project shares its information with the project's other chats. Drag chats between projects from the Chat list.</p>
        </div>
        {!selected && (
          <Button size="sm" onClick={() => setCreating(true)} className="ml-auto gap-1.5" data-testid="project-create-btn">
            <Plus className="w-4 h-4" /> New project
          </Button>
        )}
      </div>

      {creating && (
        <div className="px-8 pb-6" data-testid="project-create-form">
          <ProjectSharing mode="create" busy={busy} onSubmit={createProject} onCancel={() => setCreating(false)} />
        </div>
      )}

      {!selected ? (
        <div className="px-8 pb-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.length === 0 && (
            <div className="text-sm text-[var(--qn-foreground-muted)]" data-testid="projects-empty">No projects yet. Create one to group chats and tasks.</div>
          )}
          {projects.map((p) => <ProjectCard key={p.project_id || p.legacy_id} p={p} onOpen={open} />)}
        </div>
      ) : (
        <div className="px-8 pb-10 space-y-6" data-testid="project-detail">
          <Button variant="ghost" size="sm" onClick={back} className="gap-1.5" data-testid="project-back">
            <ArrowLeft className="w-4 h-4" /> All projects
          </Button>
          <div className="rounded-xl border border-[var(--qn-border)] bg-[var(--qn-card)] p-5">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-lg" data-testid="project-detail-name">{selected.name}</span>
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[var(--qn-surface)] text-[var(--qn-foreground-subtle)]">{selected.source}</span>
              <VisibilityBadge visibility={selected.visibility} />
            </div>
            {selected.source === "classic" && !selected.adopted && (
              <p className="text-xs text-[var(--qn-foreground-muted)] mt-1" data-testid="project-adopt-note">
                Classic project — the first chat or task you add brings it onto the governed workspace path.
              </p>
            )}
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Button size="sm" onClick={newChat} disabled={busy} className="gap-1.5" data-testid="project-new-chat">
                <MessageSquare className="w-4 h-4" /> New chat in project
              </Button>
              <input value={intent} onChange={(e) => setIntent(e.target.value)} placeholder="Describe a task for this project…"
                className="flex-1 min-w-[220px] rounded-lg border border-[var(--qn-border)] bg-[var(--qn-surface)] px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--qn-primary)]"
                data-testid="project-task-intent" onKeyDown={(e) => { if (e.key === "Enter") newTask(); }} />
              <Button size="sm" variant="outline" onClick={newTask} disabled={busy || !intent.trim()} className="gap-1.5" data-testid="project-new-task">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add task
              </Button>
            </div>
          </div>
          {selected.project_id && selected.is_owner && (
            <ProjectSharing mode="edit" busy={busy} project={selected} onSubmit={saveSharing} />
          )}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Panel title="Chats" icon={MessageSquare} empty="No chats in this project yet." testid="project-chats">
              {detail.conversations.map((c) => (
                <button key={c.conversation_id} onClick={() => navigate(`/app/chat/${c.conversation_id}`)}
                  data-testid={`project-chat-${c.conversation_id}`}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-[var(--qn-surface-hover)] text-sm">
                  {c.title || c.conversation_id}
                  <span className="block text-[11px] font-mono text-[var(--qn-foreground-subtle)]">{c.module_context} · {c.conversation_id}</span>
                </button>
              ))}
            </Panel>
            <Panel title="Tasks" icon={ListTodo} empty="No tasks in this project yet." testid="project-tasks">
              {detail.tasks.map((t) => (
                <div key={t.task_id} className="px-3 py-2 rounded-lg text-sm" data-testid={`project-task-${t.task_id}`}>
                  {t.intent}
                  <span className="block text-[11px] font-mono text-[var(--qn-foreground-subtle)]">{t.status || t.approval_state || "draft"} · {t.module_context} · {t.task_id}</span>
                </div>
              ))}
            </Panel>
          </div>
        </div>
      )}
    </div>
  );
}

export function VisibilityBadge({ visibility }) {
  const v = VISIBILITY[visibility] || { label: visibility === "project" ? "Organization" : visibility, icon: Globe2 };
  const Icon = v.icon || Lock;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[var(--qn-surface)] text-[var(--qn-foreground-subtle)]" data-testid="project-visibility">
      <Icon className="w-3 h-3" /> {v.label}
    </span>
  );
}

function ProjectCard({ p, onOpen }) {
  return (
    <button data-testid={`project-card-${p.project_id || p.legacy_id}`} onClick={() => onOpen(p)}
      className="text-left rounded-xl border border-[var(--qn-border)] bg-[var(--qn-card)] p-5 hover:border-[var(--qn-primary)]/40 transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--qn-primary)]">
      <div className="flex items-center gap-2 mb-2">
        {p.source === "classic" ? <Archive className="w-4 h-4 text-amber-500" /> : <FolderKanban className="w-4 h-4 text-[var(--qn-primary)]" />}
        <span className="font-medium truncate">{p.name}</span>
        <span className="ml-auto text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[var(--qn-surface)] text-[var(--qn-foreground-subtle)]" data-testid="project-source">{p.source}</span>
      </div>
      <div className="flex items-center gap-2 mb-3"><VisibilityBadge visibility={p.visibility} />
        {p.visibility === "members" && <span className="text-[11px] text-[var(--qn-foreground-subtle)] inline-flex items-center gap-1"><Users className="w-3 h-3" />{(p.member_ids || []).length} member(s)</span>}
      </div>
      <div className="text-sm text-[var(--qn-foreground-muted)] flex gap-4">
        <span><MessageSquare className="inline w-3.5 h-3.5 mr-1" />{p.conversation_count} chat(s)</span>
        <span><ListTodo className="inline w-3.5 h-3.5 mr-1" />{p.task_count} task(s)</span>
      </div>
    </button>
  );
}

function Panel({ title, icon: Icon, empty, testid, children }) {
  const items = React.Children.toArray(children);
  return (
    <div className="rounded-xl border border-[var(--qn-border)] bg-[var(--qn-card)] p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-sm font-semibold mb-2"><Icon className="w-4 h-4 text-[var(--qn-primary)]" />{title}</div>
      {items.length === 0 ? <div className="text-xs text-[var(--qn-foreground-subtle)]">{empty}</div> : <div className="space-y-1">{items}</div>}
    </div>
  );
}
